/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import IRenderDocument from "App/render_document_interface";
import Model from "App/model";
import {logError} from "App/debug";
import Messenger from "App/messenger";
import {RenderElement} from "App/model/elements/element";
import RenderElements from "App/model/elements/elements";
import ProjectInfo from "App/model/project_info";
import ZeropointSettings from "App/model/zeropoint_settings";
import Configuration from "App/model/configuration";
import Standpoints from "App/model/standpoints";
import Views from "App/model/views";
import LightingSettings from "App/model/lighting_settings";
import Annotations from "App/model/annotations/annotations";
import CameraAnimations from "App/model/animations/camera_animations";
import DrawSettings from "App/model/draw_settings";
import GeoLocationSettings from "App/model/geo_location_settings";
import GroupTree from "App/model/group_tree";
import {inURLBinaryData} from "App/storage/utils";
import IScene from "js_interface/src/scene_interface";

export default
class Description {

    public projectInfo: ProjectInfo;
    public zeropointSettings: ZeropointSettings;
    public geoLocationSettings: GeoLocationSettings;
    public configuration: Configuration;
    public standpoints: Standpoints;
    public views: Views;
    public lightingSettings: LightingSettings;
    public groupTree: GroupTree;
    public annotations: Annotations;
    public elements: RenderElements;
    public animations: CameraAnimations;
    public drawSettings: DrawSettings;

    private readonly sceneAPI: IScene;
    private readonly _document: IRenderDocument;
    private readonly _renderModel: Model;

    constructor(aDocument: IRenderDocument, aRenderModel: Model)
    {
        this.sceneAPI    = aDocument.getSceneAPI();
        this._document    = aDocument;
        this._renderModel = aRenderModel;

        this.projectInfo         = new ProjectInfo(this);
        this.zeropointSettings   = new ZeropointSettings(this);
        this.geoLocationSettings = new GeoLocationSettings(this);
        this.configuration       = new Configuration(this, this.sceneAPI);
        this.standpoints         = new Standpoints(this);
        this.views               = new Views(this);
        this.lightingSettings    = new LightingSettings(this, this.sceneAPI);
        this.groupTree           = new GroupTree(this);
        this.annotations         = new Annotations(this);
        this.elements            = new RenderElements(this);
        this.animations          = new CameraAnimations(this);
        this.drawSettings        = new DrawSettings(this, this.sceneAPI);
    }

    /**
     * This function parse the model description from response text for local models
     * @param aResponseText
     * @returns {boolean}
     */
    public parse(aResponseText: string): boolean {
        let modelDescriptionJSON: any = null;
        try {
            modelDescriptionJSON = JSON.parse(aResponseText);
        }
        catch (ex) {
            Messenger.fireCDWKEvent(this.sceneAPI.getTargetTag(), "cwx3d_geometryDescriptionFailed", {
                modelId: this._renderModel.getSceneAPIModelUID(),
                reason: 'FAILED: Exception when parsing the model description json response text: ' + ex
            });
            logError('FAILED: Exception when parsing the model description json response text: ' + ex);

            return false;
        }

        this.projectInfo.parse(modelDescriptionJSON); // model
        this.zeropointSettings.parse(modelDescriptionJSON); // model
        this.geoLocationSettings.parse(modelDescriptionJSON); // model
        this.drawSettings.parse(modelDescriptionJSON); // settings must be before the configuration as here it parse the outlines style
        this.configuration.parse(modelDescriptionJSON); // settings - global
        this.standpoints.parse(modelDescriptionJSON); // model
        this.views.parse(modelDescriptionJSON); // model
        // if (this._renderModel && this._renderModel.getSceneAPIModel().getIsMainModel()) {
        //     this.lightingSettings.parse(modelDescriptionJSON); // settings - global
        // }
        this.annotations.parse(modelDescriptionJSON); // model
        this.elements.parse(modelDescriptionJSON); // model
        this.animations.parse(modelDescriptionJSON); // model
        this.groupTree.parse(modelDescriptionJSON); // model

        return true;
    }

    public getSceneAPI(): IScene {
        return this.sceneAPI;
    }

    public getDocument(): IRenderDocument {
        return this._document;
    }

    public getRenderModel(): Model {
        return this._renderModel;
    }

    public getElements(): RenderElements {
        return this.elements;
    }

    public getElementsByType(aElementType: string): RenderElement[] {
        return this.elements.getElementsByType(aElementType);
    }

    public addElement(aGroupID: number, aElementTypeName: string, aElementDescriptionJSON: any): RenderElement {
        let element = this.elements.addElement(aElementTypeName, aElementDescriptionJSON);
        if (element) {
            this.groupTree.addElement(aGroupID, element);
        }

        return element;
    }

    public removeElement(aRenderElement: RenderElement) {
        this.groupTree.removeElement(aRenderElement);
        this.elements.removeElement(aRenderElement);

        aRenderElement.remove();
    }

    public getJSONRepresentation(): any {
        let result: any = {};

        result.ProjectInfo = this.projectInfo.getJSONRepresentation();
        result.ZeropointSettings   = this.zeropointSettings.getJSONRepresentation();
        result.GeoLocationSettings = this.geoLocationSettings.getJSONRepresentation();
        result.ModelConfiguration = this.configuration.getJSONRepresentation();
        result.standpoints = this.standpoints.getJSONRepresentation();
        result.views = this.views.getJSONRepresentation();
        result.LightingSettings = this.lightingSettings.getJSONRepresentation();
        result.GroupTree = this.groupTree.getJSONRepresentation();
        result.Annotations = this.annotations.getJSONRepresentation();
        result.Elements = this.elements.getJSONRepresentation();
        result.Animations = this.animations.getJSONRepresentation();
        result.ModelDrawSettings = this.drawSettings.getJSONRepresentation();

        let resultStringified = JSON.stringify(result);
        let base64Result = inURLBinaryData() + window.btoa(resultStringified);

        return base64Result;
    }
}
